import { PublicHeader } from "../../components/Header/PublicHeader";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import { NEWS_CATEGORIES } from "../../api/news";
import {
  Arrow,
  ArticleContent,
  NewsArtwork,
  NewsCard,
  NewsStatus,
  useNewsResource,
} from "./NewsComponents";
import styles from "./news.module.css";

function NewsLayout({ children }) {
  return (
    <div className={styles.page}>
      <PublicHeader sectionLabel="Медиа" className={styles.header}>
        <Link to="/cases">Кейсы</Link>
      </PublicHeader>
      <main className={styles.main}>{children}</main>
      <footer className={styles.footer}>
        <Link to="/">ЮФУ.Про</Link>
        <span>Люди, идеи и проекты Южного федерального университета</span>
        <a href="https://t.me/sfedupro" target="_blank" rel="noreferrer">
          Мы в Telegram ↗
        </a>
      </footer>
    </div>
  );
}

function usePageTitle(title) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

export function NewsPage() {
  const [params, setParams] = useSearchParams();
  const query = (params.get("q") || "").slice(0, 180);
  const category = NEWS_CATEGORIES.includes(params.get("category"))
    ? params.get("category")
    : "";
  const page = Math.min(
    100000,
    Math.max(1, Number.parseInt(params.get("page"), 10) || 1),
  );
  const resource = useNewsResource({
    query,
    category,
    limit: 9,
    offset: (page - 1) * 9,
  });
  const items = resource.data?.items || [];
  const total = resource.data?.total || 0;
  const filtered = Boolean(query || category);
  usePageTitle("Новости — ЮФУ.Про");
  const update = (changes) => {
    const next = new URLSearchParams(params);
    next.delete("page");
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    setParams(next, { replace: true });
  };
  return (
    <NewsLayout>
      <div className={styles.breadcrumb}>
        <Link to="/">Главная</Link>
        <span>/</span>
        <span>Новости</span>
      </div>
      <section className={styles.editorialHero}>
        <div className={styles.heroCopy}>
          <span className={styles.eyebrow}>
            <i /> НА СВЯЗИ, ЮФУ.ПРО
          </span>
          <h1>
            В центре
            <br />
            <span>событий.</span>
          </h1>
          <p>
            Истории, которые вдохновляют.
            <br />
            Идеи, которые становятся проектами.
          </p>
          <a href="#feed" className={styles.heroLink}>
            Узнать, что нового <span>↓</span>
          </a>
        </div>
        <NewsArtwork />
        <span className={styles.heroFootnote}>СОЗДАЁМ БУДУЩЕЕ. ВМЕСТЕ.</span>
      </section>
      <section id="feed" className={styles.feed} aria-labelledby="feed-title">
        <div className={styles.feedHeading}>
          <h2 id="feed-title">Лента новостей</h2>
          <label className={styles.search}>
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden="true"
            >
              <circle cx="10" cy="10" r="6" />
              <path d="m15 15 6 6" />
            </svg>
            <input
              type="search"
              aria-label="Поиск новостей"
              placeholder="Найти новость"
              value={query}
              maxLength={180}
              onChange={(e) => update({ q: e.target.value })}
            />
          </label>
        </div>
        <div className={styles.filters} aria-label="Категории новостей">
          {["", ...NEWS_CATEGORIES].map((value) => (
            <button
              key={value}
              aria-pressed={category === value}
              onClick={() => update({ category: value })}
            >
              {value || "Все новости"}
            </button>
          ))}
        </div>
        <NewsStatus
          resource={resource}
          empty={!filtered && !items.length && page === 1}
        />
        {!resource.loading && !resource.error && (
          <>
            {!items.length && (filtered || page > 1) && (
              <div className={styles.empty}>
                <h2>Ничего не нашлось</h2>
                <p>Попробуйте другое название или посмотрите все новости.</p>
                <button className={styles.button} onClick={() => setParams({})}>
                  Все новости
                </button>
              </div>
            )}
            <div className={styles.newsGrid}>
              {items.map((article, index) => (
                <NewsCard
                  key={article.id}
                  article={article}
                  featured={!filtered && page === 1 && index === 0}
                />
              ))}
            </div>
            {total > 9 && (
              <nav className={styles.pagination} aria-label="Страницы новостей">
                <button
                  disabled={page === 1}
                  onClick={() => update({ page: page - 1 })}
                >
                  ← Назад
                </button>
                <span>
                  {page} / {Math.ceil(total / 9)}
                </span>
                <button
                  disabled={page * 9 >= total}
                  onClick={() => update({ page: page + 1 })}
                >
                  Дальше →
                </button>
              </nav>
            )}
          </>
        )}
      </section>
    </NewsLayout>
  );
}

export function NewsArticlePage() {
  const { articleId } = useParams();
  return <ArticlePage key={articleId} articleId={articleId} />;
}

function ArticlePage({ articleId }) {
  const resource = useNewsResource({}, articleId);
  const [copied, setCopied] = useState(false);
  usePageTitle(
    resource.data ? `${resource.data.title} — ЮФУ.Про` : "Новость — ЮФУ.Про",
  );
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <NewsLayout>
      <div className={styles.breadcrumb}>
        <Link to="/news">← Все новости</Link>
      </div>
      {resource.error?.status === 404 ? (
        <div className={styles.empty}>
          <h1>Новость не найдена</h1>
          <p>Возможно, она ещё не опубликована или уже удалена.</p>
          <Link to="/news" className={styles.button}>
            К новостям
          </Link>
        </div>
      ) : (
        <NewsStatus resource={resource} />
      )}
      {resource.data && (
        <>
          <ArticleContent article={resource.data} />
          <div className={styles.articleEnd}>
            <Link to="/news">← Вернуться к новостям</Link>
            <button
              className={styles.button}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  setCopied(true);
                } catch {
                  setCopied("error");
                }
              }}
            >
              {copied === true ? "Ссылка скопирована" : "Поделиться новостью"}{" "}
              <Arrow diagonal />
            </button>
            <span role="status">
              {copied === "error"
                ? "Скопируйте адрес новости из строки браузера."
                : copied
                  ? "Ссылка готова — отправьте её друзьям."
                  : ""}
            </span>
          </div>
        </>
      )}
    </NewsLayout>
  );
}
